import { hourLabel, SHIFT_HOURS } from '../../core/clock';
import { formatMoney, formatViews } from '../../core/math';
import type { Achievement } from '../../game/achievements';
import { rankFor } from '../../game/scoring';
import { headlineFor, indexStrip, shiftLabel } from '../../game/share';
import type { GameResult } from '../../game/types';
import { h, kids, onPress } from '../dom';
import { tileRow } from './title';

export interface ResultsActions {
  share(): void;
  again(): void;
  menu(): void;
}

/**
 * End of shift: a dramatic ending card (closed / the new host / sunrise),
 * then the next morning's newspaper with the stats and a share button.
 */
export class ResultsScreen {
  readonly el: HTMLElement;
  private readonly ending: HTMLElement;
  private readonly paper: HTMLElement;
  private timers: ReturnType<typeof setTimeout>[] = [];
  /** Ignore presses right after the paper appears, so the dismissing tap can't land on a button. */
  private revealedAt = 0;
  readonly shareButton: HTMLButtonElement;
  private readonly againButton: HTMLButtonElement;
  private readonly menuButton: HTMLButtonElement;

  constructor(actions: ResultsActions) {
    this.ending = h('div', { class: 'ending', 'aria-live': 'polite' });
    this.paper = h('article', { class: 'paper' });
    this.shareButton = h('button', { class: 'cta', type: 'button' }, 'SHARE YOUR SHIFT');
    this.againButton = h(
      'button',
      { class: 'btn-secondary', type: 'button' },
      'NEXT SHIFT ▸ (calm the crowd first)',
    );
    this.menuButton = h('button', { class: 'btn-ghost', type: 'button' }, 'MENU');
    const guard = (fn: () => void) => () => {
      if (performance.now() - this.revealedAt > 400) fn();
    };
    onPress(this.shareButton, guard(actions.share));
    onPress(this.againButton, guard(actions.again));
    onPress(this.menuButton, guard(actions.menu));
    this.el = h(
      'section',
      { class: 'screen screen--results' },
      this.ending,
      h('div', { class: 'paper-wrap' }, this.paper),
    );
    this.ending.addEventListener('pointerdown', () => this.skipEnding());
  }

  private clearTimers(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }

  private skipEnding(): void {
    if (!this.el.classList.contains('is-ending')) return;
    this.clearTimers();
    this.revealPaper();
  }

  private revealPaper(): void {
    this.el.classList.remove('is-ending');
    this.el.classList.add('is-paper');
    this.revealedAt = performance.now();
    this.shareButton.focus();
  }

  show(result: GameResult, unlocked: readonly Achievement[], reducedMotion: boolean): void {
    this.clearTimers();
    this.buildPaper(result, unlocked);
    this.buildEnding(result);
    this.el.classList.remove('is-paper');
    this.el.classList.add('is-ending');
    const hold = reducedMotion
      ? 1800
      : result.hostPleased
        ? 7200
        : result.reason === 'closed'
          ? 4200
          : 3000;
    this.timers.push(setTimeout(() => this.revealPaper(), hold));
  }

  private buildEnding(r: GameResult): void {
    this.ending.dataset.kind = r.hostPleased
      ? 'host'
      : r.reason === 'closed'
        ? 'closed'
        : 'sunrise';
    if (r.hostPleased) {
      this.ending.replaceChildren(
        h('div', { class: 'ending__tile', 'aria-hidden': 'true' }, 'W'),
        h('p', { class: 'ending__big ending__type' }, 'THE WAFFLE HOUSE HAS FOUND ITS NEW HOST.'),
        h(
          'p',
          { class: 'ending__small ending__late' },
          'You never clocked out. You never will. The grill is warm. The doors don’t lock. Welcome home.',
        ),
        h('p', { class: 'ending__tap' }, 'tap to continue'),
      );
    } else if (r.reason === 'closed') {
      this.ending.replaceChildren(
        h('div', { class: 'ending__index' }, 'WAFFLE HOUSE INDEX: RED'),
        h('p', { class: 'ending__big' }, 'THIS LOCATION IS CLOSED.'),
        h(
          'p',
          { class: 'ending__small' },
          'FEMA has been notified. Somewhere, a weatherman takes off his rain jacket. The unthinkable has happened.',
        ),
        h('p', { class: 'ending__tap' }, 'tap to continue'),
      );
    } else {
      this.ending.replaceChildren(
        h('div', { class: 'ending__sun', 'aria-hidden': 'true' }),
        h('p', { class: 'ending__big' }, '6:00 AM'),
        h(
          'p',
          { class: 'ending__small' },
          'The day shift pulls in. The sign stays lit. It always does.',
        ),
        h('p', { class: 'ending__tap' }, 'tap to continue'),
      );
    }
  }

  private buildPaper(r: GameResult, unlocked: readonly Achievement[]): void {
    const rank = rankFor(r);
    const s = r.stats;
    const strip = indexStrip(r);
    const stat = (label: string, value: string): HTMLElement =>
      h('div', { class: 'stat' }, h('dt', null, label), h('dd', null, value));
    const hours =
      [...strip].length === SHIFT_HOURS
        ? [...strip]
        : Array.from({ length: SHIFT_HOURS }, (_, i) => [...strip][i] ?? '⬛');
    const date = new Date().toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    this.paper.replaceChildren(
      ...kids(
        h(
          'header',
          { class: 'paper__masthead' },
          h('div', { class: 'paper__name' }, 'The Graveyard Gazette'),
          h(
            'div',
            { class: 'paper__meta' },
            h('span', null, date),
            h('span', null, '6 AM EDITION'),
            h('span', null, shiftLabel(r)),
            h('span', null, '50¢ (+50¢ egg surcharge)'),
          ),
        ),
        h('h1', { class: 'paper__headline' }, headlineFor(r)),
        h(
          'div',
          { class: 'paper__lede' },
          h(
            'div',
            { class: 'paper__rank' },
            h('small', null, 'STAFF EVALUATION'),
            tileRow(
              rank.title.length <= 14 ? rank.title : (rank.title.split(' ').slice(-1)[0] ?? ''),
              'tile tile--sm',
            ),
            h('b', null, rank.title),
            h('p', null, rank.blurb),
          ),
          h(
            'div',
            { class: 'paper__index' },
            h('small', null, 'WAFFLE HOUSE INDEX, HOUR BY HOUR'),
            h(
              'div',
              { class: 'index-strip' },
              ...hours.map((sq, i) =>
                h(
                  'div',
                  { class: 'index-strip__cell' },
                  h('span', { class: 'index-strip__sq' }, sq),
                  h('span', { class: 'index-strip__hr' }, hourLabel(i)),
                ),
              ),
            ),
            r.femaVerdict
              ? h(
                  'p',
                  { class: 'paper__fema' },
                  `FEMA verdict for Hurricane ${r.stormName ?? '???'}: ${r.femaVerdict.toUpperCase()}`,
                )
              : null,
          ),
        ),
        h(
          'dl',
          { class: 'paper__stats' },
          stat('Tips', formatMoney(r.tips)),
          stat('Orders served', String(s.served)),
          stat('Perfect orders', String(s.perfect)),
          stat('Hash browns scattered', String(s.hashBrowns)),
          stat('Toppings applied', String(s.toppings)),
          stat('Waffles', String(s.waffles)),
          stat('Walkouts', String(s.walkouts)),
          stat('Chairs caught', s.chairsThrown ? `${s.chairsCaught} / ${s.chairsThrown}` : '—'),
          stat('Served mid-brawl', String(s.unbothered)),
          stat('Best combo', String(s.bestCombo)),
          stat('Coffee refills', String(s.coffeeRefills)),
          stat('Stream views', s.views ? formatViews(s.views) : '—'),
          ...(r.calmedBefore !== null
            ? [stat('Calmed at shift change', `${r.calmedBefore} / 3`)]
            : []),
        ),
        unlocked.length
          ? h(
              'div',
              { class: 'paper__unlocks' },
              h('small', null, 'ALSO IN THIS EDITION'),
              ...unlocked.map((a) =>
                h(
                  'div',
                  { class: 'unlock' },
                  h('span', { 'aria-hidden': 'true' }, a.emoji),
                  h('b', null, a.title),
                  h('span', null, a.description),
                ),
              ),
            )
          : null,
        h('div', { class: 'paper__actions' }, this.shareButton, this.againButton, this.menuButton),
      ),
    );
  }
}
